import type { ProposalOrder } from '@moment/horizon-data-access/proposal'
import type { ReferenceDataField } from '@moment/trading-types/instrument-data'
import type {
  AssetType,
  BondStatus,
  BondType,
  InstrumentClass,
} from '@moment/trading-types/reference-data'
import type {
  CellClassParams,
  ICellRendererParams,
  SuppressKeyboardEventParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community'
import type React from 'react'
import type { Check } from '@/trading/types/api/edit-checks'
import type { ComplianceCheck } from '@/types/api/compliance'
import type { ColumnDefinition } from './types'

import { useFeatureFlag } from '@moment/app-config-ui'
import { Badge, BadgeVariant } from '@moment/design-system-ui/badge'

import { HELD_AWAY_ACCOUNTS } from '@/app/feature-flags'
import { Routes } from '@/app/routes'
import Button, { ButtonVariant } from '@/components/atomic/button'
import Icon, { IconName } from '@/components/atomic/icon'
import { PhosphorIconName } from '@/components/atomic/icon/types'
import Link from '@/components/atomic/link'
import ProgressBar from '@/components/atomic/progress-bar'
import {
  BodyDefault,
  BodyMediumSubtext,
  BodySubtext,
  BodyXS,
  FontWeight,
} from '@/components/atomic/text'
import Tooltip from '@/components/atomic/tooltip'
import { TruncatedTextCell } from '@/components/atomic/truncated-text-cell'
import { AssetClassBadge } from '@/components/molecules/badges/asset-class'
import { AssetTypeBadge } from '@/components/molecules/badges/asset-type'
import { BondTypeBadge } from '@/components/molecules/badges/bond-type'
import { DifferenceIndicator } from '@/components/molecules/difference-indicator'
import { RatingCellRenderer } from '@/components/molecules/rating-cell-renderer'
import {
  getIdentifierPreference,
  IdentifierPreference,
} from '@/shared/helpers/identifier-preference'
import {
  getAllocationCheckActionCellRenderer,
  getAllocationCheckRejectReasonCellRenderer,
  getAllocationCheckValidationStatusCellRenderer,
} from '@/trading/trade-desk-blotter/bulk-allocation-upload/table-screen/table/helpers'
import { AllocationCheckColumnId } from '@/trading/trade-desk-blotter/bulk-allocation-upload/table-screen/table/types'
import {
  type AccountCompliance,
  ManagementType,
  type SleeveWithComplianceAndStatistics,
} from '@/types/api/accounts'
import { ColumnId } from '@/types/enums/column-id'
import {
  ProposalDetailsAccountsTableColumnId,
  ProposalDetailsOrdersTableColumnId,
} from '@/types/enums/proposals'
import {
  DateFormat,
  formatDate,
  parseISOString,
} from '@/utils/dates/dateFormatting'
import { formatIntegerToUSDate } from '@/utils/dates/dateHelpers'
import {
  getAssetClassFromOrder,
  getAssetClassLabel,
  getAssetTypeLabel,
} from '@/utils/instrument-classification'
import { maskAccountId } from '@/utils/mask-account-id'
import { getPrincipalDecimal } from '@/utils/order-calculations'
import {
  formatBondStatus,
  formatBondType,
  formatBps,
  formatQuantity,
  formatYield,
} from '@/utils/presentation/bonds'
import {
  defaultValueTransform,
  formatBoolean,
  formatDecimal,
  formatMoney,
  formatNumber,
  formatPercentage,
  formatPercentageAuto,
  formatPrice,
  isNullish,
  PLACEHOLDER,
} from '@/utils/presentation/formatting'
import { referenceDataTransformationConfig } from '@/utils/presentation/instrument-data-transforms'

import { getInputCellRenderer } from './utils'

interface ColumnCreationProps {
  cellRenderer?: string
  field?: ReferenceDataField
  headerName?: string
  hide?: boolean
  rowGroup?: boolean
  sort?: 'asc' | 'desc'
  meta?: {
    onClickApprove?: (id: string) => void
    onClickReject?: (id: string) => void
    [key: string]: unknown
  }
}

interface ColumnCreator {
  create: (props: ColumnCreationProps) => ColumnDefinition
}

export const INPUT: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? '',
      cellRenderer: (params: ICellRendererParams) => {
        return getInputCellRenderer(params.value)
      },
      suppressKeyboardEvent: (_params: SuppressKeyboardEventParams) => {
        return true
      },
      colId: ColumnId.INPUT,
    } satisfies ColumnDefinition
  },
}

// Common column formatting
const commonNumericFormat = {
  cellClass: 'ag-right-aligned-cell tabular-nums w-full flex justify-end',
  headerClass: 'ag-right-aligned-header',
}

// Required column definition
// This is reserved for generalized column types that are used in many places.
// There is no common header name for these columns.
type RequiredColumnDefinition = Required<
  Pick<ColumnDefinition, 'colId' | 'headerName' | 'field'>
> &
  Omit<ColumnDefinition, 'colId' | 'headerName' | 'field'>

// Minimal column definition
// This is reserved for specific column types that are used in many places.
// A default header name will be provided, but it can be overridden.
type MinimalColumnDefinition = Required<
  Pick<ColumnDefinition, 'colId' | 'field'>
> &
  Omit<ColumnDefinition, 'colId' | 'field'>

//
// General columns
//

// money column
export const MONEY_COLUMN = (
  props: RequiredColumnDefinition,
  options?: {
    numDigits?: number
    useZeroPlaceholder?: boolean
    addTrailingZeros?: boolean
    currencyField?: string
  },
): ColumnDefinition => ({
  minWidth: 150,
  valueFormatter: (params: ValueFormatterParams) => {
    const currency = options?.currencyField
      ? params.data?.[options.currencyField]
      : undefined
    return formatMoney(params.value, { ...options, currency })
  },
  ...commonNumericFormat,
  ...props,
})

// text column
export const TEXT_COLUMN = (
  props: RequiredColumnDefinition,
): ColumnDefinition => ({
  minWidth: 150,
  valueFormatter: (params: ValueFormatterParams) => {
    return defaultValueTransform(params.value)
  },
  ...props,
})

// instrument identifier column that displays CUSIP or ISIN based on user preference
// Note: does not require 'field' since valueGetter handles field selection dynamically
export const INSTRUMENT_ID_COLUMN = (
  props: Required<Pick<ColumnDefinition, 'colId'>> &
    Omit<ColumnDefinition, 'colId'>,
): ColumnDefinition => ({
  minWidth: 150,
  headerValueGetter: () => {
    const preference = getIdentifierPreference()
    return preference === IdentifierPreference.ISIN ? 'ISIN' : 'CUSIP'
  },
  valueGetter: (params: ValueGetterParams) => {
    const preference = getIdentifierPreference()
    const value =
      preference === IdentifierPreference.ISIN
        ? params.data?.isin
        : params.data?.cusip
    return value
  },
  cellRenderer: (params: ICellRendererParams) => {
    if (isNullish(params.value)) return <BodyDefault>{PLACEHOLDER}</BodyDefault>
    return (
      <BodyDefault asChild>
        <Link
          route={Routes.Research.detail({ bondId: params.value })}
          openInNewTab={false}
          prefetch={false}
        >
          {params.value}
        </Link>
      </BodyDefault>
    )
  },
  ...props,
})

// decimal column
export const DECIMAL_COLUMN = (
  props: RequiredColumnDefinition,
  options?: {
    numDigits?: number
  },
): ColumnDefinition => ({
  ...commonNumericFormat,
  minWidth: 150,
  valueFormatter: (params) =>
    formatDecimal(params.value, options?.numDigits || 3),
  cellRenderer: (params: ICellRendererParams) => {
    return params.valueFormatted
  },
  ...props,
})

// percentage column
export const PERCENTAGE_COLUMN = (
  props: RequiredColumnDefinition,
  options?: {
    numDigits?: number
    useZeroPlaceholder?: boolean
    removeTrailingZeros?: boolean
  },
): ColumnDefinition => ({
  ...commonNumericFormat,
  minWidth: 150,
  valueFormatter: (params) => formatPercentage(params.value, options),
  cellRenderer: (params: ICellRendererParams) => {
    return params.valueFormatted
  },
  ...props,
})

// boolean column
export const BOOLEAN_COLUMN = (
  props: RequiredColumnDefinition,
): ColumnDefinition => ({
  minWidth: 100,
  valueFormatter: (params) => formatBoolean(params.value),
  cellRenderer: (params: ICellRendererParams) => {
    return params.valueFormatted
  },
  ...props,
})

// integer column
export const INTEGER_COLUMN = (
  props: RequiredColumnDefinition,
  options?: {
    useZeroPlaceholder?: boolean
    minimumFractionDigits?: number
    maximumFractionDigits?: number
  },
): ColumnDefinition => ({
  ...commonNumericFormat,
  minWidth: 150,
  valueFormatter: (params) => {
    return formatNumber(params.value, options)
  },
  cellRenderer: (params: ICellRendererParams) => {
    return params.valueFormatted
  },
  ...props,
})

// date column
export const DATE_COLUMN = (
  props: RequiredColumnDefinition,
  dateFormat = DateFormat.SHORT,
): ColumnDefinition => ({
  minWidth: 150,
  valueFormatter: (params) => {
    if (isNullish(params.value)) return PLACEHOLDER
    return formatDate(parseISOString(params.value), dateFormat)
  },
  cellRenderer: (params: ICellRendererParams) => {
    return params.valueFormatted
  },
  ...props,
})

// price column
export const PRICE_COLUMN = (
  props: RequiredColumnDefinition,
  options?: {
    shouldTruncateIfWhole?: boolean
    useZeroPlaceholder?: boolean
  },
): ColumnDefinition => ({
  ...commonNumericFormat,
  minWidth: 150,
  valueFormatter: (params: ValueFormatterParams) => {
    return formatPrice(params.value, options)
  },
  cellRenderer: (params: ICellRendererParams) => {
    return params.valueFormatted
  },
  ...props,
})

// gain/loss column
export const GAIN_LOSS_COLUMN = (
  props: RequiredColumnDefinition,
): ColumnDefinition => ({
  ...commonNumericFormat,
  minWidth: 150,
  cellClass: (params: CellClassParams) => {
    const positiveNegativeClass =
      params.value > 0
        ? 'text-moment-green-800 dark:text-moment-green-800'
        : // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
          params.value < 0
          ? 'text-moment-red-600 dark:text-moment-red-300'
          : ''
    return `tabular-nums flex justify-end ${positiveNegativeClass}`
  },
  valueFormatter: (params: ValueFormatterParams): string => {
    if (isNullish(params.value)) return PLACEHOLDER
    return formatMoney(params.value)
  },
  ...props,
})

// Yield column
export const YIELD_COLUMN = (
  props: RequiredColumnDefinition,
): ColumnDefinition => ({
  ...commonNumericFormat,
  minWidth: 150,
  valueFormatter: (params: ValueFormatterParams): string => {
    if (isNullish(params.value)) return PLACEHOLDER
    return formatYield(params.value)
  },
  cellRenderer: (params: ICellRendererParams) => {
    return params.valueFormatted
  },
  ...props,
})

// Basis Points (BPS) column
export const BPS_COLUMN = (
  props: RequiredColumnDefinition,
): ColumnDefinition => ({
  ...commonNumericFormat,
  minWidth: 100,
  valueFormatter: (params: ValueFormatterParams): string => {
    return formatBps(params.value, 2)
  },
  cellRenderer: (params: ICellRendererParams) => {
    return params.valueFormatted
  },
  ...props,
})

// Quantity column
export const QUANTITY_COLUMN = (
  props: RequiredColumnDefinition,
): ColumnDefinition => ({
  ...commonNumericFormat,
  minWidth: 100,
  valueFormatter: (params: ValueFormatterParams): string => {
    return formatQuantity(params.value)
  },
  cellRenderer: (params: ICellRendererParams) => {
    return params.valueFormatted
  },
  ...props,
})

//
// Specific repeating data columns
//

// Rows are a fixed height, so a wrapped second line of badges is clipped rather
// than shown — it renders as slivers of badges above and below the row. Keep
// them on one line and roll the remainder into a count.
const MAX_VISIBLE_BOND_TYPES = 2

// bond type column
export const defaultBondTypeRenderer = (
  params: ICellRendererParams,
): React.ReactNode => {
  const value = params.value
  if (Array.isArray(value)) {
    const visible = value.slice(0, MAX_VISIBLE_BOND_TYPES)
    const hiddenCount = value.length - visible.length
    return (
      <span
        className="flex min-w-0 items-center gap-1 overflow-hidden"
        title={value.map(formatBondType).join(', ')}
      >
        {visible.map((bondType: BondType) => (
          <BondTypeBadge key={bondType} bondType={bondType} />
        ))}
        {hiddenCount > 0 && (
          <Badge variant={BadgeVariant.Neutral}>+{hiddenCount}</Badge>
        )}
      </span>
    )
  }
  return <BondTypeBadge bondType={value} />
}

export const BOND_TYPE_COLUMN = (
  props: MinimalColumnDefinition,
): ColumnDefinition => ({
  headerName: 'Asset class',
  minWidth: 125,
  cellRenderer: defaultBondTypeRenderer,
  filter: 'agSetColumnFilter',
  filterValueGetter: (params: ValueGetterParams): string => {
    return formatBondType(params.data.bondType as BondType)
  },
  ...props,
})

type AssetLabelRowData = {
  assetType: AssetType | null
  bondType: BondType | null
  instrumentClass: InstrumentClass | null
}

const getAssetClassLabelForRow = (data: AssetLabelRowData): string | null =>
  getAssetClassLabel(getAssetClassFromOrder(data))

const getAssetTypeLabelForRow = (data: AssetLabelRowData): string | null =>
  getAssetTypeLabel(getAssetClassFromOrder(data))

const defaultAssetClassRenderer = (
  params: ICellRendererParams,
): React.ReactNode => {
  const label = getAssetClassLabelForRow(params.data)
  if (!label) return null
  return <AssetClassBadge label={label} />
}

type AssetClassColumnDefinition = Required<Pick<ColumnDefinition, 'colId'>> &
  Omit<ColumnDefinition, 'colId'>

export const ASSET_CLASS_COLUMN = (
  props: AssetClassColumnDefinition,
): ColumnDefinition => ({
  headerName: 'Asset class',
  minWidth: 125,
  cellRenderer: defaultAssetClassRenderer,
  valueGetter: (params: ValueGetterParams): string | null =>
    getAssetClassLabelForRow(params.data),
  filter: 'agSetColumnFilter',
  ...props,
})

const defaultAssetTypeRenderer = (
  params: ICellRendererParams,
): React.ReactNode => {
  const label = getAssetTypeLabelForRow(params.data)
  if (!label) return null
  return <AssetTypeBadge label={label} />
}

type AssetTypeColumnDefinition = Required<Pick<ColumnDefinition, 'colId'>> &
  Omit<ColumnDefinition, 'colId'>

export const ASSET_TYPE_COLUMN = (
  props: AssetTypeColumnDefinition,
): ColumnDefinition => ({
  headerName: 'Asset type',
  minWidth: 125,
  cellRenderer: defaultAssetTypeRenderer,
  valueGetter: (params: ValueGetterParams): string | null =>
    getAssetTypeLabelForRow(params.data),
  filter: 'agSetColumnFilter',
  ...props,
})

// Rating column renderer (supports both SP and Moody's)
// This returns the RatingCellRenderer component which handles permissions internally
export const defaultRatingRenderer = (
  params: ICellRendererParams,
): React.ReactNode => {
  return <RatingCellRenderer {...params} />
}

// Rating column - automatically handles permissions for both display and header
export const RATING_COLUMN = (
  props: MinimalColumnDefinition,
): ColumnDefinition => ({
  headerName: 'Rating',
  minWidth: 120,
  cellRenderer: defaultRatingRenderer,
  // Mark this as a rating column so we can identify it later
  cellRendererParams: {
    ...props.cellRendererParams,
    isRatingColumn: true,
  },
  ...props,
})

export const AVERAGE_RATING_COLUMN = (
  props: MinimalColumnDefinition,
): ColumnDefinition => ({
  headerName: 'Avg Rating',
  minWidth: 120,
  cellRenderer: defaultRatingRenderer,
  cellRendererParams: {
    ...props.cellRendererParams,
    isRatingColumn: true,
    isAverageRating: true,
  },
  ...props,
})

// duration column
export const DURATION_COLUMN = (
  props: MinimalColumnDefinition,
): ColumnDefinition =>
  DECIMAL_COLUMN(
    {
      ...commonNumericFormat,
      headerName: 'Duration',
      minWidth: 150,
      ...props,
    },
    {
      numDigits: 4,
    },
  )

// description column
export const DESCRIPTION_COLUMN = (
  props: MinimalColumnDefinition,
): ColumnDefinition =>
  TEXT_COLUMN({
    headerName: 'Description',
    minWidth: 300,
    ...props,
  })

// coupon column
export const COUPON_COLUMN = (
  props: MinimalColumnDefinition,
): ColumnDefinition =>
  PERCENTAGE_COLUMN(
    {
      headerName: 'Coupon',
      minWidth: 150,
      ...props,
    },
    {
      numDigits: 3,
    },
  )

// bond status column
export const defaultBondStatusRenderer = (
  params: ICellRendererParams,
): React.ReactNode => {
  const displayValue = formatBondStatus(params.value as BondStatus)
  switch (params.value as BondStatus) {
    // TODO: Add BadgeVariants for each BondStatus
    default:
      return <Badge variant={BadgeVariant.Neutral}>{displayValue}</Badge>
  }
}

export const BOND_STATUS_COLUMN = (
  props: MinimalColumnDefinition,
): ColumnDefinition => ({
  headerName: 'Status',
  minWidth: 100,
  cellRenderer: defaultBondStatusRenderer,
  ...props,
})

export const PROPOSAL_ORDER_SELECT: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    const PINNED_CELL_WIDTH = 80

    return {
      headerName: props.headerName ?? '',
      hide: props.hide,
      colId: ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_SELECT,
      pinned: 'right',
      sortable: false,
      minWidth: PINNED_CELL_WIDTH,
      maxWidth: PINNED_CELL_WIDTH,
      width: PINNED_CELL_WIDTH,
      context: {
        handleOrderRemoveAction: undefined,
        originalOrderId: undefined,
        selectedOrderId: undefined,
      },
      cellRenderer: (params: ICellRendererParams) => {
        // For all bonds in the alternative bonds table, always show the Select button
        const handleOrderRemoveAction = params.colDef?.context
          ?.handleOrderRemoveAction as (order: ProposalOrder) => void

        // Check if this is the original bond
        const isOriginalBond =
          params.data?.moment_order_id ===
          params.colDef?.context?.originalOrderId

        // Check if this is the selected bond
        const isSelectedBond =
          params.data?.moment_order_id ===
          params.colDef?.context?.selectedOrderId

        // For original bond, show static "Original" text
        if (isOriginalBond) {
          return (
            <div className="w-full flex items-center justify-center">
              <BodySubtext className="text-secondary">Original</BodySubtext>
            </div>
          )
        }

        // For selected bond, show static "Selected" text
        if (isSelectedBond) {
          return (
            <div className="w-full flex items-center justify-center">
              <BodySubtext
                fontWeight={FontWeight.Medium}
                className="text-primary-500"
              >
                Selected
              </BodySubtext>
            </div>
          )
        }

        // For all other bonds, show the Select button
        return (
          <div className="w-full flex items-center justify-center">
            <Button
              label="Select"
              variant={ButtonVariant.Secondary}
              onClick={() => {
                if (handleOrderRemoveAction) {
                  handleOrderRemoveAction(params.data)
                }
              }}
            />
          </div>
        )
      },
      cellStyle: {
        position: 'absolute',
        top: '0px',
        right: '0px',
        zIndex: 10,
        overflow: 'visible',
        '--pinned-cell-width': `${PINNED_CELL_WIDTH}px`,
      },
    } satisfies ColumnDefinition
  },
}

//
// Orders table columns
//
export const PROPOSAL_ORDER_DESCRIPTION: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'Description',
      field: 'instrument.description',
      hide: props.hide,
      minWidth: 300,
      colId: ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_DESCRIPTION,
      cellRenderer: (params: ICellRendererParams) => {
        const description = isNullish(params.value)
          ? params.data.instrument.description
          : params.value
        return (
          <div className="z-10">
            <Tooltip
              simpleText={params.data.instrument.description}
              side="right"
            >
              <div className="truncate">{description}</div>
            </Tooltip>
          </div>
        )
      },
    } satisfies ColumnDefinition
  },
}

export const PROPOSAL_ORDER_MATURITY_DATE: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'Maturity date',
      field: 'instrument.maturity_date',
      hide: props.hide,
      colId: ColumnId.MATURITY_DATE,
      minWidth: 120,
      valueFormatter: (params: ValueFormatterParams) => {
        if (isNullish(params.value)) return PLACEHOLDER
        return formatDate(parseISOString(params.value), 'MM/dd/yyyy')
      },
    } satisfies ColumnDefinition
  },
}

export const PROPOSAL_ORDER_ISIN: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'ISIN',
      field: 'instrument.isin',
      hide: props.hide,
      minWidth: 115,
      cellClass: 'tabular-nums',
      colId: ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_ISIN,
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <BodyMediumSubtext>{params.data.instrument.isin}</BodyMediumSubtext>
        )
      },
    } satisfies ColumnDefinition
  },
}

export const PROPOSAL_ORDER_SECTOR: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'Sector',
      field: 'instrument.sector',
      hide: props.hide,
      minWidth: 150,
      colId: ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_SECTOR,
      valueFormatter: (params: ValueFormatterParams) => {
        if (isNullish(params.value)) return PLACEHOLDER
        return referenceDataTransformationConfig.sector.valueTransformation(
          params.value,
        )
      },
    } satisfies ColumnDefinition
  },
}

export const PROPOSAL_ORDER_AMOUNT_AND_QUANTITY: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'Total $/Qty',
      field: 'quantity',
      hide: props.hide,
      minWidth: 130,
      cellClass: 'ag-right-aligned-cell tabular-nums flex justify-end',
      headerClass: 'ag-right-aligned-header',
      colId: ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_QUANTITY,
      cellRenderer: (params: ICellRendererParams) => {
        const totalMarketValue = getPrincipalDecimal({
          quantity: params.data.quantity,
          price: params.data.limit_price,
        })

        return (
          <div className="flex flex-col">
            <BodySubtext>
              {formatMoney(Number(totalMarketValue), {
                numDigits: 2,
                useZeroPlaceholder: true,
              })}
            </BodySubtext>
            <BodyXS className="text-secondary">
              {formatQuantity(params.data.quantity)}
            </BodyXS>
          </div>
        )
      },
    } satisfies ColumnDefinition
  },
}

export const PROPOSAL_ORDER_LIMIT_YTW: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'Limit YTW',
      field: 'limit_ytw',
      hide: props.hide,
      minWidth: 120,
      cellClass:
        'ag-right-aligned-cell border-left tabular-nums flex justify-end w-full',
      headerClass: 'ag-right-aligned-header',
      colId: ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_LIMIT_YTW,
      cellRenderer: (params: ICellRendererParams) => {
        const formattedYTW =
          params.data.limit_ytw === 0
            ? PLACEHOLDER
            : formatPercentage(params.data.limit_ytw, {
                numDigits: 3,
                useZeroPlaceholder: true,
              })
        return (
          <div className="flex flex-col">
            <BodySubtext>{formattedYTW}</BodySubtext>
          </div>
        )
      },
    } satisfies ColumnDefinition
  },
}

export const PROPOSAL_ORDER_EVAL_YTW: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'Eval mid YTW',
      field: 'instrument.yield_to_worst',
      hide: props.hide,
      minWidth: 120,
      cellClass: 'ag-right-aligned-cell tabular-nums flex justify-end',
      headerClass: 'ag-right-aligned-header',
      colId: ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_EVAL_YTW,
      cellRenderer: (params: ICellRendererParams) => {
        const formattedYTW =
          params.data.instrument.yield_to_worst === 0
            ? PLACEHOLDER
            : formatPercentage(params.data.instrument.yield_to_worst, {
                numDigits: 3,
                useZeroPlaceholder: true,
              })
        return (
          <div className="flex flex-col">
            <BodySubtext>{formattedYTW}</BodySubtext>
          </div>
        )
      },
    } satisfies ColumnDefinition
  },
}

export const PROPOSAL_ORDER_EVAL_YTW_DIFFERENCE: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'Difference',
      field: 'instrument.yield_to_worst',
      hide: props.hide,
      minWidth: 100,
      cellClass: 'ag-right-aligned-cell tabular-nums flex justify-end',
      headerClass: 'ag-right-aligned-header',
      colId:
        ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_EVAL_YTW_DIFFERENCE,
      cellRenderer: (params: ICellRendererParams) => {
        const yieldDifference =
          params.data.instrument.yield_to_worst - params.data.limit_ytw

        const formattedYTW = formatYield(yieldDifference, true)

        const formattedYieldDifference = yieldDifference > 0 ? '+' : ''

        return (
          <div className="flex flex-col">
            <BodySubtext>
              {formattedYieldDifference}
              {formattedYTW}
            </BodySubtext>
          </div>
        )
      },
    } satisfies ColumnDefinition
  },
}

export const PROPOSAL_ORDER_BOND_SWAP_LIMIT_PRICE: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'Limit price',
      field: 'limit_price',
      hide: props.hide,
      minWidth: 120,
      cellClass:
        'ag-right-aligned-cell border-left tabular-nums flex justify-end',
      headerClass: 'ag-right-aligned-header',
      colId:
        ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_BOND_SWAP_LIMIT_PRICE,
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <div className="flex flex-col">
            <BodySubtext>
              {formatMoney(params.data.limit_price, {
                numDigits: 2,
                useZeroPlaceholder: true,
              })}
            </BodySubtext>
          </div>
        )
      },
    } satisfies ColumnDefinition
  },
}

export const PROPOSAL_ORDER_BOND_SWAP_EVAL_PRICE: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'Eval mid price',
      field: 'instrument.price',
      hide: props.hide,
      minWidth: 110,
      cellClass: 'ag-right-aligned-cell tabular-nums flex justify-end',
      headerClass: 'ag-right-aligned-header',
      colId:
        ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_BOND_SWAP_EVAL_PRICE,
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <div className="flex flex-col">
            <BodySubtext>
              {formatMoney(params.data.instrument.price, {
                numDigits: 2,
                useZeroPlaceholder: true,
              })}
            </BodySubtext>
          </div>
        )
      },
    } satisfies ColumnDefinition
  },
}

export const PROPOSAL_BOND_SWAP_ORDER_PRICE_DIFFERENCE: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'Difference',
      field: 'price_diff',
      hide: props.hide,
      minWidth: 130,
      cellClass: 'ag-right-aligned-cell tabular-nums flex justify-end',
      headerClass: 'ag-right-aligned-header',
      colId:
        ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_BOND_SWAP_PRICE_DIFFERENCE,
      cellRenderer: (params: ICellRendererParams) => {
        const priceDifference =
          params.data.limit_price - params.data.instrument.price

        return (
          <div className="flex flex-col">
            <BodySubtext>
              {priceDifference > 0 ? '+' : ''}
              {formatMoney(priceDifference, {
                numDigits: 2,
                useZeroPlaceholder: true,
              })}
            </BodySubtext>
          </div>
        )
      },
    } satisfies ColumnDefinition
  },
}

export const PROPOSAL_ORDER_DURATION: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'Duration',
      field: 'instrument.duration',
      hide: props.hide,
      minWidth: 110,
      cellClass: 'ag-right-aligned-cell tabular-nums flex justify-end',
      headerClass: 'ag-right-aligned-header',
      colId: ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_DURATION,
      valueFormatter: (params: ValueFormatterParams) => {
        if (isNullish(params.value)) return PLACEHOLDER
        return formatNumber(params.value, {
          minimumFractionDigits: 3,
          maximumFractionDigits: 3,
          useZeroPlaceholder: true,
        })
      },
    } satisfies ColumnDefinition
  },
}

//
// Proposal Accounts Table
//

// Type for the object being validated
interface DataObject {
  // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
  [key: string]: any
}

// Helper to get nested value safely with proper typing
const getNestedValue = <T extends DataObject>(
  obj: T,
  path: string,
): unknown => {
  return path.split('.').reduce((curr: unknown, key: string) => {
    if (curr === null || curr === undefined) return undefined
    return (curr as DataObject)[key]
  }, obj)
}

// Improved field validator that only checks specific paths with proper typing
const hasRequiredFields = (
  params: ICellRendererParams,
  requiredFields: string[],
): boolean => {
  return requiredFields.every((path) => {
    const value = getNestedValue(params, path)
    if (value === null || value === undefined) return false
    if (value === '') return false
    if (Array.isArray(value) && value.length === 0) return false
    return true
  })
}

export const PROPOSAL_ACCOUNT_DETAILS: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'Account',
      field: 'after.account_name',
      hide: props.hide,
      colId: ProposalDetailsAccountsTableColumnId.PROPOSAL_ACCOUNT_DETAILS,
      minWidth: 180,
      cellRenderer: (params: ICellRendererParams) => {
        const requiredFields = ['data.type', 'data.after.account_id']
        if (!hasRequiredFields(params, requiredFields)) return null
        if (params.data.type === 'sleeve') return null

        const accountId = params.data.after.account_id
        const maskedAccountId = maskAccountId(accountId)

        return (
          <div className="flex flex-col">
            <BodySubtext fontWeight={FontWeight.Medium}>
              {params.data.before.account_name}
            </BodySubtext>
            <BodyXS className="text-secondary">{maskedAccountId}</BodyXS>
          </div>
        )
      },
    } satisfies ColumnDefinition
  },
}

export const PROPOSAL_ACCOUNT_MODEL: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'Model',
      field: 'after.sleeves',
      hide: props.hide,
      minWidth: 200,
      colId: ProposalDetailsAccountsTableColumnId.PROPOSAL_ACCOUNT_MODEL,
      cellRenderer: 'agGroupCellRenderer',
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: (params: ICellRendererParams) => {
          const requiredFields = ['data.type', 'data.after.sleeves']
          if (!hasRequiredFields(params, requiredFields)) return null
          if (params.data.type === 'sleeve') {
            return <TruncatedTextCell text={params.data.name} />
          }

          const sleeves: SleeveWithComplianceAndStatistics[] = Object.values(
            params.data.after.sleeves ?? {},
          )

          if (sleeves.length === 0) {
            return PLACEHOLDER
          }
          // SMA accounts should only show the one model name
          if (params.data.after.management_type === ManagementType.Sma) {
            return (
              <TruncatedTextCell
                // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
                text={sleeves[0]!.name}
                className="text-primary"
              />
            )
          }
          if (sleeves.length === 1) {
            return (
              <TruncatedTextCell
                // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
                text={sleeves[0]!.name}
                className="text-primary"
              />
            )
          }
          return (
            <BodySubtext className="text-primary">
              {`${sleeves.length} Models`}
            </BodySubtext>
          )
        },
      },
    } satisfies ColumnDefinition
  },
}

export const PROPOSAL_ACCOUNT_PROPOSED_TRADE_COUNT: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'Proposed',
      hide: props.hide,
      minWidth: 120,
      colId:
        ProposalDetailsAccountsTableColumnId.PROPOSAL_ACCOUNT_PROPOSED_TRADE_COUNT,
      cellRenderer: (params: ICellRendererParams) => {
        const requiredFields = ['data.type', 'data.allocations']
        if (!hasRequiredFields(params, requiredFields)) return PLACEHOLDER
        // Skip rendering for sleeve rows
        if (params.data.type === 'sleeve') return PLACEHOLDER

        const numAllocations = params.data.allocations?.length || 0
        return (
          <BodySubtext>
            {numAllocations}{' '}
            {numAllocations === 1 ? 'allocation' : 'allocations'}
          </BodySubtext>
        )
      },
    } satisfies ColumnDefinition
  },
}

export const PROPOSAL_ACCOUNT_NEW_FI_WEIGHT: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'New FI weight',
      hide: props.hide,
      minWidth: 115,
      cellClass: 'border-left',
      colId:
        ProposalDetailsAccountsTableColumnId.PROPOSAL_ACCOUNT_NEW_FI_WEIGHT,
      cellRenderer: (params: ICellRendererParams) => {
        const requiredFields = ['data.type', 'data.before', 'data.after']
        if (!hasRequiredFields(params, requiredFields)) return null

        if (params.data.type === 'sleeve') {
          const beforeSleeve = params.data.before.sleeves[params.data.sleeveId]
          const afterSleeve = params.data.after.sleeves[params.data.sleeveId]

          const beforeFIValue =
            beforeSleeve?.statistics?.total_market_value || 0
          const beforeTotalValue =
            params.data.accountStatistics?.total_market_value || 0
          const beforeWeight = beforeTotalValue
            ? beforeFIValue / beforeTotalValue
            : 0

          const afterFIValue = afterSleeve?.statistics?.total_market_value || 0
          const afterTotalValue =
            params.data.accountStatistics?.total_market_value || 0
          const afterWeight = afterTotalValue
            ? afterFIValue / afterTotalValue
            : 0

          return (
            <div className="flex flex-col">
              <BodySubtext fontWeight={FontWeight.Medium}>
                {formatPercentageAuto(afterWeight * 100)}
              </BodySubtext>
              <BodyXS className="text-secondary">
                {formatPercentageAuto(beforeWeight * 100)}
              </BodyXS>
            </div>
          )
        }

        // Account-level calculation
        const beforeTotalValue =
          params.data.before.statistics?.total_market_value || 0

        const beforeFIValue =
          params.data.before.statistics?.tracked_fixed_income_market_value || 0

        const beforeWeight = beforeTotalValue
          ? beforeFIValue / beforeTotalValue
          : 0

        const afterTotalValue =
          params.data.after.statistics?.total_market_value || 0
        const afterFIValue =
          params.data.after.statistics?.tracked_fixed_income_market_value || 0
        const afterWeight = afterTotalValue ? afterFIValue / afterTotalValue : 0

        return (
          <div className="flex flex-col">
            <BodySubtext fontWeight={FontWeight.Medium}>
              {formatPercentageAuto(afterWeight * 100)}
            </BodySubtext>
            <BodyXS className="text-secondary">
              {formatPercentageAuto(beforeWeight * 100)}
            </BodyXS>
          </div>
        )
      },
    } satisfies ColumnDefinition
  },
}

export const PROPOSAL_ACCOUNT_NEW_FI_WEIGHT_DIFFERENCE: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'Difference',
      hide: props.hide,
      minWidth: 95,
      colId:
        ProposalDetailsAccountsTableColumnId.PROPOSAL_ACCOUNT_NEW_FI_WEIGHT,
      cellRenderer: (params: ICellRendererParams) => {
        const requiredFields = ['data.type', 'data.before', 'data.after']

        if (!hasRequiredFields(params, requiredFields)) return null

        if (params.data.type === 'sleeve') {
          const beforeSleeve = params.data.before.sleeves[params.data.sleeveId]
          const afterSleeve = params.data.after.sleeves[params.data.sleeveId]

          const beforeFIValue =
            beforeSleeve?.statistics?.total_market_value || 0
          const beforeTotalValue =
            params.data.accountStatistics?.total_market_value || 0
          const beforeWeight = beforeTotalValue
            ? beforeFIValue / beforeTotalValue
            : 0

          const afterFIValue = afterSleeve?.statistics?.total_market_value || 0
          const afterTotalValue =
            params.data.accountStatistics?.total_market_value || 0
          const afterWeight = afterTotalValue
            ? afterFIValue / afterTotalValue
            : 0
          const weightDiff = Number(
            ((afterWeight - beforeWeight) * 100).toFixed(3),
          )

          return (
            <DifferenceIndicator
              difference={weightDiff}
              formatter={(value) => formatPercentageAuto(value, 3)}
            />
          )
        }

        // Account-level calculation
        const beforeFIValue =
          params.data.before.statistics?.tracked_fixed_income_market_value || 0
        const beforeTotalValue =
          params.data.before.statistics?.total_market_value || 0
        const beforeWeight = beforeTotalValue
          ? beforeFIValue / beforeTotalValue
          : 0

        const afterFIValue =
          params.data.after.statistics?.tracked_fixed_income_market_value || 0
        const afterTotalValue =
          params.data.after.statistics?.total_market_value || 0
        const afterWeight = afterTotalValue ? afterFIValue / afterTotalValue : 0
        if (isNullish(beforeWeight) || isNullish(afterWeight)) {
          return PLACEHOLDER
        }
        const weightDiff = (afterWeight - beforeWeight) * 100

        return (
          <DifferenceIndicator
            difference={weightDiff}
            formatter={(value) => formatPercentageAuto(value)}
          />
        )
      },
    } satisfies ColumnDefinition
  },
}

function ViolationCount({ count }: { count: number }) {
  if (count === 0) return <BodySubtext>{count}</BodySubtext>
  return (
    <div className="flex items-center gap-1">
      <Icon
        iconName={IconName.ExclamationTriangleIcon}
        className="text-moment-red-700 scale-90"
      />
      <BodySubtext>{count}</BodySubtext>
    </div>
  )
}

// Helper function to count violations in compliance checks
const countViolations = (complianceData: AccountCompliance) => {
  if (!complianceData?.checks) return 0

  return complianceData.checks.reduce((total, check: ComplianceCheck) => {
    if (!check.compliant) {
      return total + 1
    }
    return total
  }, 0)
}

export const PROPOSAL_ACCOUNT_VIOLATIONS: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'Violations',
      hide: props.hide,
      minWidth: 105,
      cellClass: 'border-left',
      colId: ProposalDetailsAccountsTableColumnId.PROPOSAL_ACCOUNT_VIOLATIONS,
      cellRenderer: (params: ICellRendererParams) => {
        const requiredFields = ['data.type', 'data.before', 'data.after']
        if (!hasRequiredFields(params, requiredFields)) return null
        if (params.data.type === 'sleeve') {
          const beforeSleeve = params.data.before.sleeves[params.data.sleeveId]
          const afterSleeve = params.data.after.sleeves[params.data.sleeveId]

          const beforeViolations = countViolations(beforeSleeve?.compliance)
          const afterViolations = countViolations(afterSleeve?.compliance)

          return (
            <div className="flex items-center justify-between gap-2">
              <ViolationCount count={beforeViolations} />
              <Icon iconName={IconName.ArrowRightIcon} className="scale-90" />
              <ViolationCount count={afterViolations} />
            </div>
          )
        }

        const beforeViolations = countViolations(params.data.before.compliance)
        const afterViolations = countViolations(params.data.after.compliance)

        return (
          <div className="flex items-center justify-between gap-2">
            <ViolationCount count={beforeViolations} />
            <Icon iconName={IconName.ArrowRightIcon} className="scale-90" />
            <ViolationCount count={afterViolations} />
          </div>
        )
      },
    } satisfies ColumnDefinition
  },
}

export const PROPOSAL_ACCOUNT_VIOLATIONS_DIFFERENCE: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'Difference',
      hide: props.hide,
      minWidth: 95,
      colId:
        ProposalDetailsAccountsTableColumnId.PROPOSAL_ACCOUNT_VIOLATIONS_DIFFERENCE,
      cellRenderer: (params: ICellRendererParams) => {
        const requiredFields = ['data.type', 'data.before', 'data.after']
        if (!hasRequiredFields(params, requiredFields)) return null
        if (params.data.type === 'sleeve') {
          const beforeSleeve = params.data.before.sleeves[params.data.sleeveId]
          const afterSleeve = params.data.after.sleeves[params.data.sleeveId]

          const beforeViolations = countViolations(beforeSleeve?.compliance)
          const afterViolations = countViolations(afterSleeve?.compliance)

          const violationDiff = afterViolations - beforeViolations

          return <DifferenceIndicator difference={violationDiff} invert />
        }

        const beforeViolations = countViolations(params.data.before.compliance)
        const afterViolations = countViolations(params.data.after.compliance)

        const violationDiff = afterViolations - beforeViolations

        return (
          <DifferenceIndicator
            difference={violationDiff}
            formatter={(value) => value.toString()}
            invert
          />
        )
      },
    } satisfies ColumnDefinition
  },
}

export const PROPOSAL_ACCOUNT_NEW_AVG_YTW: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'New avg YTW',
      hide: props.hide,
      minWidth: 130,
      cellClass: 'border-left',
      colId: ProposalDetailsAccountsTableColumnId.PROPOSAL_ACCOUNT_NEW_AVG_YTW,
      cellRenderer: (params: ICellRendererParams) => {
        const requiredFields = ['data.type', 'data.before', 'data.after']
        if (!hasRequiredFields(params, requiredFields)) return null
        if (params.data.type === 'sleeve') {
          const beforeSleeve = params.data.before.sleeves[params.data.sleeveId]
          const afterSleeve = params.data.after.sleeves[params.data.sleeveId]

          const beforeAvgYTW = beforeSleeve?.statistics?.average_ytw
          const afterAvgYTW = afterSleeve?.statistics?.average_ytw

          return (
            <div className="flex flex-col">
              <BodySubtext fontWeight={FontWeight.Medium}>
                {formatPercentageAuto(afterAvgYTW)}
              </BodySubtext>
              <BodyXS className="text-secondary">
                {formatPercentageAuto(beforeAvgYTW)}
              </BodyXS>
            </div>
          )
        }

        const beforeAvgYTW = params.data.before.statistics.average_ytw
        const afterAvgYTW = params.data.after.statistics.average_ytw
        return (
          <div className="flex flex-col">
            <BodySubtext fontWeight={FontWeight.Medium}>
              {formatPercentageAuto(afterAvgYTW)}
            </BodySubtext>
            <BodyXS className="text-secondary">
              {formatPercentageAuto(beforeAvgYTW)}
            </BodyXS>
          </div>
        )
      },
    } satisfies ColumnDefinition
  },
}

export const PROPOSAL_ACCOUNT_NEW_AVG_YTW_DIFFERENCE: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'Difference',
      hide: props.hide,
      minWidth: 95,
      colId:
        ProposalDetailsAccountsTableColumnId.PROPOSAL_ACCOUNT_NEW_AVG_YTW_DIFFERENCE,
      cellRenderer: (params: ICellRendererParams) => {
        const requiredFields = ['data.type', 'data.before', 'data.after']
        if (!hasRequiredFields(params, requiredFields)) return null
        if (params.data.type === 'sleeve') {
          const beforeSleeve = params.data.before.sleeves[params.data.sleeveId]
          const afterSleeve = params.data.after.sleeves[params.data.sleeveId]

          const beforeAvgYTW = beforeSleeve?.statistics?.average_ytw
          const afterAvgYTW = afterSleeve?.statistics?.average_ytw

          const avgYTWDiff = afterAvgYTW - beforeAvgYTW

          return (
            <DifferenceIndicator
              difference={avgYTWDiff}
              formatter={(value) => formatPercentageAuto(value)}
            />
          )
        }

        const beforeAvgYTW = params.data.before.statistics.average_ytw
        const afterAvgYTW = params.data.after.statistics.average_ytw

        const avgYTWDiff = afterAvgYTW - beforeAvgYTW

        return (
          <DifferenceIndicator
            difference={avgYTWDiff}
            formatter={(value) => formatPercentageAuto(value)}
          />
        )
      },
    } satisfies ColumnDefinition
  },
}

export const PROPOSAL_ACCOUNT_NEW_AVG_SP_RATING: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'New avg S&P rating',
      hide: props.hide,
      minWidth: 140,
      cellClass: 'border-left',
      colId:
        ProposalDetailsAccountsTableColumnId.PROPOSAL_ACCOUNT_NEW_AVG_SP_RATING,
      cellRenderer: (params: ICellRendererParams) => {
        const requiredFields = ['data.type', 'data.before', 'data.after']
        if (!hasRequiredFields(params, requiredFields)) return null
        if (params.data.type === 'sleeve') {
          const beforeSleeve = params.data.before.sleeves[params.data.sleeveId]
          const afterSleeve = params.data.after.sleeves[params.data.sleeveId]

          const beforeAvgSPRating = beforeSleeve?.statistics?.average_sp_rating
          const afterAvgSPRating = afterSleeve?.statistics?.average_sp_rating

          return (
            <div className="flex flex-col">
              <BodySubtext fontWeight={FontWeight.Medium}>
                {isNullish(afterAvgSPRating) ? PLACEHOLDER : afterAvgSPRating}
              </BodySubtext>
              <BodyXS className="text-secondary">
                {isNullish(beforeAvgSPRating) ? PLACEHOLDER : beforeAvgSPRating}
              </BodyXS>
            </div>
          )
        }

        const beforeAvgSPRating =
          params.data.before.statistics.average_sp_rating
        const afterAvgSPRating = params.data.after.statistics.average_sp_rating
        return (
          <div className="flex flex-col">
            <BodySubtext fontWeight={FontWeight.Medium}>
              {isNullish(afterAvgSPRating) ? PLACEHOLDER : afterAvgSPRating}
            </BodySubtext>
            <BodyXS className="text-secondary">
              {isNullish(beforeAvgSPRating) ? PLACEHOLDER : beforeAvgSPRating}
            </BodyXS>
          </div>
        )
      },
    } satisfies ColumnDefinition
  },
}

export const PROPOSAL_ACCOUNT_NEW_AVG_COUPON: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'New avg coupon',
      hide: props.hide,
      minWidth: 130,
      cellClass: 'border-left',
      colId:
        ProposalDetailsAccountsTableColumnId.PROPOSAL_ACCOUNT_NEW_AVG_COUPON,
      cellRenderer: (params: ICellRendererParams) => {
        const requiredFields = ['data.type', 'data.before', 'data.after']
        if (!hasRequiredFields(params, requiredFields)) return null
        if (params.data.type === 'sleeve') {
          const beforeSleeve = params.data.before.sleeves[params.data.sleeveId]
          const afterSleeve = params.data.after.sleeves[params.data.sleeveId]

          const beforeAvgCoupon = beforeSleeve?.statistics?.average_coupon
          const afterAvgCoupon = afterSleeve?.statistics?.average_coupon

          return (
            <div className="flex flex-col">
              <BodySubtext fontWeight={FontWeight.Medium}>
                {formatPercentage(afterAvgCoupon, { numDigits: 3 })}
              </BodySubtext>
              <BodyXS className="text-secondary">
                {formatPercentage(beforeAvgCoupon, { numDigits: 3 })}
              </BodyXS>
            </div>
          )
        }

        const beforeAvgCoupon = params.data.before.statistics.average_coupon
        const afterAvgCoupon = params.data.after.statistics.average_coupon
        return (
          <div className="flex flex-col">
            <BodySubtext fontWeight={FontWeight.Medium}>
              {formatPercentage(afterAvgCoupon, { numDigits: 3 })}
            </BodySubtext>
            <BodyXS className="text-secondary">
              {formatPercentage(beforeAvgCoupon, { numDigits: 3 })}
            </BodyXS>
          </div>
        )
      },
    } satisfies ColumnDefinition
  },
}

export const PROPOSAL_ACCOUNT_NEW_AVG_COUPON_DIFFERENCE: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'Difference',
      hide: props.hide,
      minWidth: 95,
      colId:
        ProposalDetailsAccountsTableColumnId.PROPOSAL_ACCOUNT_NEW_AVG_COUPON_DIFFERENCE,
      cellRenderer: (params: ICellRendererParams) => {
        const requiredFields = ['data.type', 'data.before', 'data.after']
        if (!hasRequiredFields(params, requiredFields)) return null
        if (params.data.type === 'sleeve') {
          const beforeSleeve = params.data.before.sleeves[params.data.sleeveId]
          const afterSleeve = params.data.after.sleeves[params.data.sleeveId]

          const beforeAvgCoupon = beforeSleeve?.statistics?.average_coupon
          const afterAvgCoupon = afterSleeve?.statistics?.average_coupon

          const couponDiff = afterAvgCoupon - beforeAvgCoupon

          return (
            <DifferenceIndicator
              difference={couponDiff}
              formatter={(value) =>
                formatPercentage(value, { numDigits: couponDiff === 0 ? 0 : 3 })
              }
            />
          )
        }

        const beforeAvgCoupon = params.data.before.statistics.average_coupon
        const afterAvgCoupon = params.data.after.statistics.average_coupon
        const couponDiff = afterAvgCoupon - beforeAvgCoupon
        return (
          <DifferenceIndicator
            difference={couponDiff}
            formatter={(value) =>
              formatPercentage(value, {
                numDigits: couponDiff === 0 ? 0 : 3,
              })
            }
          />
        )
      },
    } satisfies ColumnDefinition
  },
}

export const PROPOSAL_ACCOUNT_NEW_AVG_MATURITY: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'New avg mat.',
      hide: props.hide,
      minWidth: 130,
      cellClass: 'border-left',
      colId:
        ProposalDetailsAccountsTableColumnId.PROPOSAL_ACCOUNT_NEW_AVG_MATURITY,
      cellRenderer: (params: ICellRendererParams) => {
        const requiredFields = ['data.type', 'data.before', 'data.after']
        if (!hasRequiredFields(params, requiredFields)) return null
        if (params.data.type === 'sleeve') {
          const beforeSleeve = params.data.before.sleeves[params.data.sleeveId]
          const afterSleeve = params.data.after.sleeves[params.data.sleeveId]

          const beforeAvgMaturity = beforeSleeve?.statistics?.average_maturity
          const afterAvgMaturity = afterSleeve?.statistics?.average_maturity

          return (
            <div className="flex flex-col">
              <BodySubtext fontWeight={FontWeight.Medium}>
                {formatNumber(afterAvgMaturity, {
                  minimumFractionDigits: 3,
                  maximumFractionDigits: 3,
                })}
              </BodySubtext>
              <BodyXS className="text-secondary">
                {formatNumber(beforeAvgMaturity, {
                  minimumFractionDigits: 3,
                  maximumFractionDigits: 3,
                })}
              </BodyXS>
            </div>
          )
        }

        const beforeAvgMaturity = params.data.before.statistics.average_maturity
        const afterAvgMaturity = params.data.after.statistics.average_maturity
        return (
          <div className="flex flex-col">
            <BodySubtext fontWeight={FontWeight.Medium}>
              {formatNumber(afterAvgMaturity, {
                minimumFractionDigits: 3,
                maximumFractionDigits: 3,
              })}
            </BodySubtext>
            <BodyXS className="text-secondary">
              {formatNumber(beforeAvgMaturity, {
                minimumFractionDigits: 3,
                maximumFractionDigits: 3,
              })}
            </BodyXS>
          </div>
        )
      },
    } satisfies ColumnDefinition
  },
}

export const PROPOSAL_ACCOUNT_NEW_AVG_MATURITY_DIFFERENCE: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'Difference',
      hide: props.hide,
      minWidth: 95,
      colId:
        ProposalDetailsAccountsTableColumnId.PROPOSAL_ACCOUNT_NEW_AVG_MATURITY_DIFFERENCE,
      cellRenderer: (params: ICellRendererParams) => {
        const requiredFields = ['data.type', 'data.before', 'data.after']
        if (!hasRequiredFields(params, requiredFields)) return null
        if (params.data.type === 'sleeve') {
          const beforeSleeve = params.data.before.sleeves[params.data.sleeveId]
          const afterSleeve = params.data.after.sleeves[params.data.sleeveId]

          const beforeAvgMaturity = beforeSleeve?.statistics?.average_maturity
          const afterAvgMaturity = afterSleeve?.statistics?.average_maturity

          const maturityDiff = afterAvgMaturity - beforeAvgMaturity

          return <DifferenceIndicator difference={maturityDiff} />
        }

        const beforeAvgMaturity = params.data.before.statistics.average_maturity
        const afterAvgMaturity = params.data.after.statistics.average_maturity
        return (
          <DifferenceIndicator
            difference={afterAvgMaturity - beforeAvgMaturity}
          />
        )
      },
    } satisfies ColumnDefinition
  },
}

export const PROPOSAL_ACCOUNT_NEW_AVG_DURATION: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'New avg duration',
      hide: props.hide,
      minWidth: 130,
      cellClass: 'border-left',
      colId:
        ProposalDetailsAccountsTableColumnId.PROPOSAL_ACCOUNT_NEW_AVG_DURATION,
      cellRenderer: (params: ICellRendererParams) => {
        const requiredFields = ['data.type', 'data.before', 'data.after']
        if (!hasRequiredFields(params, requiredFields)) return null
        if (params.data.type === 'sleeve') {
          const beforeSleeve = params.data.before.sleeves[params.data.sleeveId]
          const afterSleeve = params.data.after.sleeves[params.data.sleeveId]

          const beforeAvgDuration = beforeSleeve?.statistics?.average_duration
          const afterAvgDuration = afterSleeve?.statistics?.average_duration

          return (
            <div className="flex flex-col">
              <BodySubtext fontWeight={FontWeight.Medium}>
                {formatNumber(afterAvgDuration, {
                  minimumFractionDigits: 3,
                  maximumFractionDigits: 3,
                })}
              </BodySubtext>
              <BodyXS className="text-secondary">
                {formatNumber(beforeAvgDuration, {
                  minimumFractionDigits: 3,
                  maximumFractionDigits: 3,
                })}
              </BodyXS>
            </div>
          )
        }

        const beforeAvgDuration = params.data.before.statistics.average_duration
        const afterAvgDuration = params.data.after.statistics.average_duration
        return (
          <div className="flex flex-col">
            <BodySubtext fontWeight={FontWeight.Medium}>
              {formatNumber(afterAvgDuration, {
                minimumFractionDigits: 3,
                maximumFractionDigits: 3,
              })}
            </BodySubtext>
            <BodyXS className="text-secondary">
              {formatNumber(beforeAvgDuration, {
                minimumFractionDigits: 3,
                maximumFractionDigits: 3,
              })}
            </BodyXS>
          </div>
        )
      },
    } satisfies ColumnDefinition
  },
}

export const PROPOSAL_ACCOUNT_NEW_AVG_DURATION_DIFFERENCE: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'Difference',
      hide: props.hide,
      minWidth: 95,
      colId:
        ProposalDetailsAccountsTableColumnId.PROPOSAL_ACCOUNT_NEW_AVG_DURATION_DIFFERENCE,
      cellRenderer: (params: ICellRendererParams) => {
        const requiredFields = ['data.type', 'data.before', 'data.after']
        if (!hasRequiredFields(params, requiredFields)) return null
        if (params.data.type === 'sleeve') {
          const beforeSleeve = params.data.before.sleeves[params.data.sleeveId]
          const afterSleeve = params.data.after.sleeves[params.data.sleeveId]

          const beforeAvgDuration = beforeSleeve?.statistics?.average_duration
          const afterAvgDuration = afterSleeve?.statistics?.average_duration

          const durationDiff = afterAvgDuration - beforeAvgDuration

          return <DifferenceIndicator difference={durationDiff} />
        }

        const beforeAvgDuration = params.data.before.statistics.average_duration
        const afterAvgDuration = params.data.after.statistics.average_duration
        return (
          <DifferenceIndicator
            difference={afterAvgDuration - beforeAvgDuration}
          />
        )
      },
    } satisfies ColumnDefinition
  },
}

export const PROPOSAL_ACCOUNT_TOTAL_CASH: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'Total cash',
      hide: props.hide,
      minWidth: 130,
      cellClass: 'border-left',
      colId: ProposalDetailsAccountsTableColumnId.PROPOSAL_ACCOUNT_TOTAL_CASH,
      cellRenderer: (params: ICellRendererParams) => {
        const requiredFields = ['data.type', 'data.before', 'data.after']
        if (!hasRequiredFields(params, requiredFields)) return null
        if (params.data.type === 'sleeve') {
          const beforeSleeve = params.data.before.sleeves[params.data.sleeveId]
          const afterSleeve = params.data.after.sleeves[params.data.sleeveId]

          const beforeTotalCash = beforeSleeve?.statistics?.cash_market_value
          const afterTotalCash = afterSleeve?.statistics?.cash_market_value

          return (
            <div className="flex flex-col">
              <BodySubtext>{formatMoney(afterTotalCash)}</BodySubtext>
              <BodyXS className="text-secondary">
                {formatMoney(beforeTotalCash)}
              </BodyXS>
            </div>
          )
        }

        const beforeTotalCash = params.data.before.statistics.cash_market_value
        const afterTotalCash = params.data.after.statistics.cash_market_value
        return (
          <div className="flex flex-col">
            <BodySubtext>{formatMoney(afterTotalCash)}</BodySubtext>
            <BodyXS className="text-secondary">
              {formatMoney(beforeTotalCash)}
            </BodyXS>
          </div>
        )
      },
    } satisfies ColumnDefinition
  },
}

export const PROPOSAL_ACCOUNT_TOTAL_CASH_DIFFERENCE: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'Difference',
      hide: props.hide,
      minWidth: 140,
      colId:
        ProposalDetailsAccountsTableColumnId.PROPOSAL_ACCOUNT_TOTAL_CASH_DIFFERENCE,
      cellRenderer: (params: ICellRendererParams) => {
        const requiredFields = ['data.type', 'data.before', 'data.after']
        if (!hasRequiredFields(params, requiredFields)) return null
        if (params.data.type === 'sleeve') {
          const beforeSleeve = params.data.before.sleeves[params.data.sleeveId]
          const afterSleeve = params.data.after.sleeves[params.data.sleeveId]

          const beforeTotalCash = beforeSleeve?.statistics?.cash_market_value
          const afterTotalCash = afterSleeve?.statistics?.cash_market_value

          const totalCashDiff = afterTotalCash - beforeTotalCash
          return (
            <DifferenceIndicator
              difference={totalCashDiff}
              formatter={(value) => formatMoney(value)}
            />
          )
        }
        const beforeTotalCash = params.data.before.statistics.cash_market_value
        const afterTotalCash = params.data.after.statistics.cash_market_value
        const totalCashDiff = afterTotalCash - beforeTotalCash
        return (
          <DifferenceIndicator
            difference={totalCashDiff}
            formatter={(value) => formatMoney(value)}
          />
        )
      },
    } satisfies ColumnDefinition
  },
}

export const PROPOSAL_ACCOUNT_TRADES_PROGRESS: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'Trades',
      hide: props.hide,
      minWidth: 150,
      maxWidth: 200,
      colId:
        ProposalDetailsAccountsTableColumnId.PROPOSAL_ACCOUNT_TRADES_PROGRESS,
      cellRenderer: (params: ICellRendererParams) => {
        const requiredFields = ['data.type', 'data.allocations']
        if (!hasRequiredFields(params, requiredFields)) return PLACEHOLDER
        if (params.data.type === 'sleeve') return PLACEHOLDER

        const { num_allocations = 0, num_filled_allocations = 0 } = params.data

        const fillProgress =
          num_allocations > 0
            ? (num_filled_allocations / num_allocations) * 100
            : 0

        return (
          <div className="flex flex-col w-full gap-1">
            <BodySubtext>
              {formatPercentage(fillProgress, { numDigits: 0 })}
            </BodySubtext>
            <ProgressBar value={fillProgress} />
          </div>
        )
      },
    } satisfies ColumnDefinition
  },
}

export const ALLOCATION_CHECK_SUBFIRM: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      field: 'subfirm',
      headerName: 'Subfirm',
      colId: AllocationCheckColumnId.SUBFIRM,
      minWidth: 90,
      maxWidth: 90,
      filter: 'agSetColumnFilter',
      cellRenderer: (params: ICellRendererParams) => {
        return defaultValueTransform(params.value)
      },
      ...props,
    } satisfies ColumnDefinition
  },
}

export const ALLOCATION_CHECK_ACCOUNT_ID: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      field: 'account_id',
      headerName: 'Account ID',
      colId: AllocationCheckColumnId.ACCOUNT_ID,
      minWidth: 120,
      maxWidth: 120,
      filter: 'agTextColumnFilter',
      valueFormatter: (params: ValueFormatterParams) => {
        return defaultValueTransform(params.value)
      },
      ...props,
    } satisfies ColumnDefinition
  },
}

export const ALLOCATION_CHECK_VALIDATION_STATUS: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'Validation',
      hide: props.hide,
      cellRenderer: getAllocationCheckValidationStatusCellRenderer,
      cellClass: 'w-full border-r border-amber-500',
      minWidth: 120,
      maxWidth: 120,
      colId: AllocationCheckColumnId.VALIDATION_STATUS,
      filter: 'agSetColumnFilter',
      filterValueGetter: (params: ValueGetterParams) => {
        return params.data?.manualStatus ?? params.data?.serverStatus
      },
    } satisfies ColumnDefinition
  },
}

export const ALLOCATION_CHECK_INSTRUMENT_ID: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      field: 'instrument_id',
      headerName: 'CUSIP',
      colId: AllocationCheckColumnId.INSTRUMENT_ID,
      minWidth: 120,
      maxWidth: 120,
      hide: props.hide,
      filter: 'agTextColumnFilter',
      valueFormatter: (params: ValueFormatterParams) => {
        return defaultValueTransform(params.value)
      },
    } satisfies ColumnDefinition
  },
}

export const ALLOCATION_CHECK_REJECT_REASON: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      field: 'reject_reason',
      headerName: 'Reject reason',
      colId: AllocationCheckColumnId.REJECT_REASON,
      hide: props.hide,
      filter: 'agSetColumnFilter',
      cellClass: 'border-left',
      cellRenderer: getAllocationCheckRejectReasonCellRenderer,
      filterValueGetter: (params: ValueGetterParams) => {
        return [
          params.data?.error_message,
          // biome-ignore lint/correctness/noUnsafeOptionalChaining: Bulk suppression as part of biome migration
          ...params.data?.checks?.map((c: Check) => c.reason),
        ].filter((reason) => !!reason)
      },
    } satisfies ColumnDefinition
  },
}

export const ALLOCATION_CHECK_PRICE: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    const valueFormatter = (params: ValueFormatterParams) => {
      const price = params.data?.price
      if (typeof price !== 'number') {
        return 'Market'
      }
      return formatPrice(price)
    }
    return {
      headerName: props.headerName ?? 'Limit price',
      field: 'price',
      minWidth: 100,
      maxWidth: 100,
      hide: props.hide,
      colId: AllocationCheckColumnId.LIMIT_PRICE,
      filter: 'agNumberColumnFilter',
      valueFormatter,
      cellClass: 'justify-end',
      ...props,
    } satisfies ColumnDefinition
  },
}

export const ALLOCATION_CHECK_QUANTITY: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'Quantity',
      field: 'quantity.value',
      minWidth: 80,
      maxWidth: 80,
      hide: props.hide,
      colId: AllocationCheckColumnId.QUANTITY,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params: ValueFormatterParams) => {
        return formatQuantity(params.value, {
          isAlreadyInNumBonds: true,
        })
      },
      cellClass: 'justify-end',
      ...props,
    } satisfies ColumnDefinition
  },
}

export const ALLOCATION_CHECK_SETTLEMENT_DATE: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'Settle date',
      field: 'settlementDate',
      minWidth: 100,
      maxWidth: 100,
      hide: props.hide,
      colId: AllocationCheckColumnId.SETTLEMENT_DATE,
      filter: 'agTextColumnFilter',
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value === null || params.value === undefined) return '-'
        return formatIntegerToUSDate(params.value) ?? '-'
      },
      ...props,
    } satisfies ColumnDefinition
  },
}

export const ALLOCATION_CHECK_ACTION_COLUMN: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: '',
      minWidth: 80,
      width: 80,
      maxWidth: 80,
      hide: props.hide,
      colId: AllocationCheckColumnId.ACTION_COLUMN,
      sortable: false,
      filter: false,
      lockPinned: true,
      pinned: 'right',
      cellClass: 'w-fit flex justify-center -translate-x-3',
      cellRenderer: (params: ICellRendererParams) => {
        return getAllocationCheckActionCellRenderer(params, props?.meta)
      },
    } satisfies ColumnDefinition
  },
}

export const NOTES_COLUMN = (
  props: RequiredColumnDefinition,
  options?: {
    notesFieldPath?: string
    iconSize?: 'small' | 'medium' | 'large'
  },
): ColumnDefinition => ({
  maxWidth: 40,
  minWidth: 40,
  sortable: false,
  filter: false,
  cellRenderer: (params: ICellRendererParams) => {
    const noteContent = params.data?.extra?.['_moment.notes']
    if (isNullish(noteContent) || !noteContent.trim()) {
      return null
    }

    return (
      <div className="flex items-center justify-center w-full">
        <Tooltip
          simpleText={noteContent}
          maxWidth={1200}
          className="whitespace-pre-wrap"
        >
          <span className="flex items-center cursor-pointer">
            <Icon
              iconName={PhosphorIconName.NoteIcon}
              isPhosphor={true}
              className="text-secondary"
              size={options?.iconSize || 'large'}
            />
          </span>
        </Tooltip>
      </div>
    )
  },
  ...props,
})

function DoNotTradeStatusCell({
  doNotTrade,
  heldAway,
}: {
  doNotTrade: boolean
  heldAway: boolean
}) {
  const heldAwayEnabled = useFeatureFlag(HELD_AWAY_ACCOUNTS)
  const paused = doNotTrade || (heldAwayEnabled && heldAway)

  return (
    <div className="flex items-center justify-center w-full">
      {paused ? (
        <Icon
          iconName={IconName.PauseCircleIcon}
          className="text-moment-red-700"
        />
      ) : (
        <Icon
          iconName={IconName.PlayCircleIcon}
          className="text-moment-green-900"
        />
      )}
    </div>
  )
}

export const DO_NOT_TRADE_STATUS_COLUMN = (
  props: RequiredColumnDefinition,
): ColumnDefinition => ({
  maxWidth: 60,
  minWidth: 60,
  cellRenderer: (params: ICellRendererParams) => (
    <DoNotTradeStatusCell
      doNotTrade={params.data?.do_not_trade ?? false}
      heldAway={params.data?.held_away === true}
    />
  ),
  ...props,
})
